import {
  ActionContext,
  ActionTree,
  GetterTree,
  Module,
  MutationTree,
} from "vuex";
import { State as RootState } from "../state";
import { AuthState } from "./authState";
import User from "../../models/user";
import {
  ACCESS_TOKEN,
  CODE_CHALLENGE,
  CODE_VERIFIER,
  EXPIRES_AT,
  ID_TOKEN,
  REFRESH_TOKEN,
  PROFILE,
  USER_INFO,
} from "../../utils/constants";
import { generatePkceCodes } from "@/utils/authUtils";
import { getTokenFromAuthCode, getUserInfo } from "@/api/auth";

type AuthContext = ActionContext<AuthState, RootState>;

const state: AuthState = {
  accessToken: localStorage.getItem(ACCESS_TOKEN),
  codeChallenge: localStorage.getItem(CODE_CHALLENGE),
  codeVerifier: localStorage.getItem(CODE_VERIFIER),
  expiresAt: localStorage.getItem(EXPIRES_AT),
  isLoading: false,
  loginError: null,
  isLoggedIn: !!localStorage.getItem(ACCESS_TOKEN),
  idToken: localStorage.getItem(ID_TOKEN),
  profile: User.fromLocalStorageJSON(
    JSON.parse(localStorage.getItem(PROFILE) || "{}")
  ),
  userInfo: localStorage.getItem(USER_INFO),
  refreshToken: localStorage.getItem(REFRESH_TOKEN),
};

const getters: GetterTree<AuthState, RootState> = {
  getAccessToken(state: AuthState): string | null {
    return state.accessToken;
  },

  getCodeChallenge(state: AuthState): string | null {
    return state.codeChallenge;
  },

  getCodeVerifier(state: AuthState): string | null {
    return state.codeVerifier;
  },

  getExpiresAt(state: AuthState): string | null {
    return state.expiresAt;
  },

  getIdToken(state: AuthState): string | null {
    return state.idToken;
  },

  getIsLoggedIn(state: AuthState): boolean | null {
    return state.isLoggedIn;
  },

  getLoginError(state: AuthState): string | null {
    return state.loginError;
  },

  getProfile(state: AuthState): User | null {
    return state.profile;
  },

  getRefreshToken(state: AuthState): string | null {
    return state.refreshToken;
  },

  isAuthenticated(state: AuthState): boolean {
    return state.isLoggedIn;
  },

  isLoading(state: AuthState): boolean {
    return state.isLoading;
  },
};

const actions: ActionTree<AuthState, RootState> = {
  async logout(context: AuthContext) {
    context.commit("logoutSuccess");
  },

  async initializeStore(context: AuthContext) {
    context.commit("initStore");
  },

  async fetchToken(context: AuthContext, { authCode, verifier }) {
    const codeVerifier = verifier;
    try {
      const authDetails = await getTokenFromAuthCode(authCode, codeVerifier);
      context.commit("loginSuccess", authDetails);
      const accessToken = await authDetails.access_token;
      const profileDetail = await getUserInfo(accessToken);
      context.commit("fetchProfileSuccess", profileDetail);
    } catch (error) {
      context.commit("loginFailure", { error });
    }
  },
};

const mutations: MutationTree<AuthState> = {
  initStore(state: AuthState) {
    if (!state.accessToken && !state.codeChallenge) {
      // Construct a new code challenge & verifier
      const pkce = generatePkceCodes();
      state.codeChallenge = pkce.codeChallenge;
      state.codeVerifier = pkce.codeVerifier;
      localStorage.setItem(CODE_VERIFIER, pkce.codeVerifier);
      localStorage.setItem(CODE_CHALLENGE, pkce.codeChallenge);
    }

    const notExpiredYet =
      Number(state.expiresAt) - Math.floor(Date.now() / 1000) > 0;

    if (!state.codeChallenge && notExpiredYet) {
      const pkce = generatePkceCodes();
      state.codeChallenge = pkce.codeChallenge;
      state.codeVerifier = pkce.codeVerifier;
      localStorage.setItem(CODE_VERIFIER, pkce.codeVerifier);
      localStorage.setItem(CODE_CHALLENGE, pkce.codeChallenge);
    }
  },

  fetchProfileSuccess(state: AuthState, profile) {
    localStorage.setItem(USER_INFO, JSON.stringify(profile));
    state.userInfo = JSON.stringify(profile);
  },

  loginSuccess(
    state: AuthState,
    { access_token, refresh_token, id_token, expires_in }
  ) {
    localStorage.setItem(ACCESS_TOKEN, access_token);
    state.isLoading = false;
    state.accessToken = access_token;

    if (refresh_token) {
      state.refreshToken = refresh_token;
      localStorage.setItem(REFRESH_TOKEN, refresh_token);
    }

    state.codeVerifier = null;
    state.codeChallenge = null;
    state.isLoggedIn = true;
    state.idToken = id_token;

    // Calculate when the token expires
    const currentTime = Math.floor(Date.now() / 1000);
    const expiresAt = currentTime + expires_in;
    state.expiresAt = expiresAt;

    // Cache values in local storage
    localStorage.setItem(ACCESS_TOKEN, access_token);
    localStorage.setItem(ID_TOKEN, id_token);
    localStorage.setItem(EXPIRES_AT, expiresAt.toString());

    // Remove PKCE Codes from localStorage
    localStorage.removeItem(CODE_VERIFIER);
    localStorage.removeItem(CODE_CHALLENGE);
  },

  loginFailure(state: AuthState, { error }) {
    state.isLoading = false;
    state.loginError = error;
  },

  logoutSuccess(state: AuthState) {
    state.accessToken = null;
    state.profile = null;
    state.refreshToken = null;
    state.idToken = null;
    state.expiresAt = null;
    state.isLoggedIn = false;
    localStorage.removeItem(PROFILE);
    localStorage.removeItem(ACCESS_TOKEN);
    localStorage.removeItem(REFRESH_TOKEN);
    localStorage.removeItem(ID_TOKEN);
    localStorage.removeItem(EXPIRES_AT);
    localStorage.removeItem(USER_INFO);
  },
};

export const auth: Module<AuthState, RootState> = {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
